import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';
import { getAccessToken } from '../redux/auth/auth.selectors';
import { getErrorMessage } from '../utils/common.utils';
import { createStandardOrganizationGroupsApi, runThunkWithAuth } from '../redux/utils/api.utils';

interface UseOrganizationGroupSearchOptions {
  searchOnMount?: boolean;
}

interface OrganizationGroupOption {
  id: string;
  name: string;
}

export const ORGANIZATION_GROUP_SEARCH_LIMIT = 20;

export const useOrganizationGroupSearch = ({ searchOnMount = true }: UseOrganizationGroupSearchOptions = {}) => {
  const dispatch = useDispatch();
  const accessToken = useSelector(getAccessToken);
  const organizationGroupsApi = useMemo(
    () =>
      createStandardOrganizationGroupsApi(
        {
          auth: { token: accessToken },
        } as Parameters<typeof createStandardOrganizationGroupsApi>[0],
        dispatch
      ),
    [accessToken, dispatch]
  );
  const [searchTerm, setSearchTerm] = useState<string | undefined>(searchOnMount ? '' : undefined);
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [organizationGroups, setOrganizationGroups] = useState<OrganizationGroupOption[]>([]);
  const isSearchLoading = isLoading || searchTerm !== debouncedSearchTerm;

  useEffect(() => {
    if (debouncedSearchTerm === undefined) return;

    let isCurrent = true;

    setIsLoading(true);
    setError(undefined);

    const search = async () => {
      try {
        const apiResponse = await runThunkWithAuth(
          () => organizationGroupsApi.searchOrganizationGroups(debouncedSearchTerm, ORGANIZATION_GROUP_SEARCH_LIMIT),
          dispatch
        );

        if (isCurrent) {
          setOrganizationGroups(apiResponse.data);
        }
      } catch (e) {
        if (isCurrent) {
          setError(getErrorMessage(e));
          setOrganizationGroups([]);
        }
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    };

    search();

    return () => {
      isCurrent = false;
    };
  }, [debouncedSearchTerm, dispatch, organizationGroupsApi]);

  return {
    isLoading: isSearchLoading,
    organizationGroups,
    error,
    searchOrganizationGroups: setSearchTerm,
  };
};
